import type { ApiResponse, HttpMethod, QueryParams } from '@nomiss/types';
import { apiConfig } from '@nomiss/configs';
import { ERROR_MESSAGES } from '@nomiss/constants';
import { buildApiHeaders } from './sdk-client';

/**
 * API Service for making HTTP requests
 */
class ApiService {
  private baseUrl: string;
  private defaultHeaders: Record<string, string>;

  constructor() {
    // Allow construction even when API URL is not set so that code importing
    // this module (e.g. during Next.js build/prerender) does not throw.
    // We validate the URL lazily in buildUrl before making a network request.
    this.baseUrl = apiConfig.baseUrl ?? '';
    this.defaultHeaders = { ...apiConfig.defaultHeaders };
  }

  /**
   * Set authorization token
   */
  setAuthToken(token: string) {
    this.defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  /**
   * Remove authorization token
   */
  removeAuthToken() {
    delete this.defaultHeaders['Authorization'];
  }

  /**
   * Build URL with query parameters
   */
  private buildUrl(endpoint: string, params?: QueryParams): string {
    if (!this.baseUrl) {
      throw new Error('API URL is not set');
    }

    const url = new URL(endpoint, this.baseUrl);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    return url.toString();
  }

  /**
   * Make HTTP request
   */
  private async request<T>(
    method: HttpMethod,
    endpoint: string,
    options?: {
      body?: unknown;
      params?: QueryParams;
      headers?: Record<string, string>;
    },
  ): Promise<ApiResponse<T>> {
    try {
      const url = this.buildUrl(endpoint, options?.params);

      const body = options?.body ? JSON.stringify(options.body) : undefined;

      const response = await fetch(url, {
        method,
        headers: buildApiHeaders({
          ...(body ? { 'Content-Type': 'application/json' } : {}),
          ...options?.headers,
        }),
        body,
        credentials: apiConfig.withCredentials ? 'include' : 'omit',
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: {
            message: data.message || ERROR_MESSAGES.SERVER_ERROR,
            code: data.code,
            details: data.details,
          },
        };
      }

      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          message: ERROR_MESSAGES.NETWORK_ERROR,
          details: error,
        },
      };
    }
  }

  /**
   * GET request
   */
  async get<T>(
    endpoint: string,
    params?: QueryParams,
  ): Promise<ApiResponse<T>> {
    return this.request<T>('GET', endpoint, { params });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>('POST', endpoint, { body });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', endpoint, { body });
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', endpoint, { body });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', endpoint);
  }
}

export const apiService = new ApiService();
